import { TRPCLink, httpBatchLink } from "@trpc/client";
import { observable } from "@trpc/server/observable";
import { AnyRouter } from "@trpc/server";
import { tokenStorage } from "../../utils/auth/tokenStorage";
import config from "../../config/environment";
import {
  ExponentialBackoff,
  isRetryableError,
} from "../../utils/exponentialBackoff";

let isRefreshingToken = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

const refreshAuthToken = async (): Promise<string> => {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  const backoff = new ExponentialBackoff({
    maxRetries: 3,
    initialDelay: 1000,
    maxDelay: 10000,
  });

  let lastError: any;

  while (!backoff.isExhausted()) {
    try {
      const response = await fetch(`${config.api.url}/trpc/auth.refreshToken`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Refresh-Token": refreshToken,
        },
        body: JSON.stringify({
          json: { refreshToken },
        }),
      });

      if (!response.ok) {
        const error = new Error("Failed to refresh token");
        (error as any).response = { status: response.status };
        throw error;
      }

      const data = await response.json();
      const result = data.result?.data?.json;

      if (!result?.accessToken && !result?.token) {
        throw new Error("Invalid refresh response");
      }

      // Handle both possible response formats
      const newAccessToken = result.accessToken || result.token;
      const newRefreshToken = result.refreshToken;

      tokenStorage.setAuthToken(newAccessToken);

      // Always update refresh token if provided (token rotation)
      if (newRefreshToken) {
        tokenStorage.setRefreshToken(newRefreshToken);
      }

      return newAccessToken;
    } catch (error) {
      lastError = error;

      // Don't retry on 401 (invalid refresh token)
      if ((error as any).response?.status === 401) {
        throw error;
      }

      // Check if error is retryable
      if (!isRetryableError(error)) {
        throw error;
      }

      if (!backoff.isExhausted()) {
        const delay = backoff.getNextDelay();
        console.log(
          `Retrying token refresh after ${delay}ms (attempt ${backoff.getAttempt()})`,
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError || new Error("Failed to refresh token after retries");
};

export const authRefreshLink =
  <TRouter extends AnyRouter>(): TRPCLink<TRouter> =>
  () => {
    return ({ next, op }) => {
      return observable((observer) => {
        let retryCount = 0;
        const maxRetries = 1;

        const executeOperation = (token?: string) => {
          const headers: Record<string, string> = {
            ...(op.context.headers || {}),
          };

          if (token) {
            headers.Authorization = `Bearer ${token}`;
          } else {
            const authToken = tokenStorage.getAuthToken();
            if (authToken) {
              headers.Authorization = `Bearer ${authToken}`;
            }
          }

          const subscription = next({
            ...op,
            context: {
              ...op.context,
              headers,
            },
          }).subscribe({
            next(value) {
              observer.next(value);
            },
            error(err) {
              const httpStatus = err?.data?.httpStatus;

              if (
                httpStatus === 401 &&
                retryCount < maxRetries &&
                tokenStorage.getRefreshToken() &&
                !op.path.includes("auth.refreshToken")
              ) {
                retryCount++;

                if (!isRefreshingToken) {
                  isRefreshingToken = true;

                  refreshAuthToken()
                    .then((newToken) => {
                      onTokenRefreshed(newToken);
                      executeOperation(newToken);
                    })
                    .catch((refreshError) => {
                      tokenStorage.clearTokens();
                      window.dispatchEvent(new CustomEvent("auth:expired"));
                      observer.error(refreshError);
                    })
                    .finally(() => {
                      isRefreshingToken = false;
                    });
                } else {
                  subscribeTokenRefresh((newToken) => {
                    executeOperation(newToken);
                  });
                }
              } else {
                observer.error(err);
              }
            },
            complete() {
              observer.complete();
            },
          });

          return () => {
            subscription.unsubscribe();
          };
        };

        return executeOperation();
      });
    };
  };

export function createTrpcLinksWithRefresh() {
  return [
    authRefreshLink(),
    httpBatchLink({
      url: `${config.api.url}${config.api.trpcEndpoint}`,
      headers: () => {
        const headers: Record<string, string> = {};
        const token = tokenStorage.getAuthToken();
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
        return headers;
      },
    }),
  ];
}
